/**
 * ModuleInterfaceSagas
 *
 * Purpose:
 * This saga file serves as a bridge between the Community Edition (CE) and Enterprise Edition (EE)
 * module-related functionalities. It provides a clean interface layer that handles all interactions
 * between core widget operations and module-specific features available in the enterprise version.
 */
export * from "ce/sagas/moduleInterfaceSagas";
import type { ReduxAction } from "actions/ReduxActionTypes";
import type { HandleModuleWidgetCreationSagaPayload } from "ce/sagas/moduleInterfaceSagas";
import { createUIModuleInstance } from "ee/actions/moduleInstanceActions";
import { ModuleInstanceCreatorType } from "ee/constants/ModuleInstanceConstants";
import {
  ReduxActionErrorTypes,
  ReduxActionTypes,
} from "ee/constants/ReduxActionConstants";
import {
  getIsModuleFetchingEntities,
  getIsModuleReferenceUpdating,
} from "ee/selectors/modulesSelector";
import { getIsPackageEditorInitialized } from "ee/selectors/packageSelectors";
import { appsmithTelemetry } from "instrumentation";
import log from "loglevel";
import type { Saga } from "redux-saga";
import { call, put, select, take } from "redux-saga/effects";
import { getWidgets } from "sagas/selectors";
import { getCurrentPageId } from "selectors/editorSelectors";

export function* handleModuleWidgetCreationSaga(
  props: HandleModuleWidgetCreationSagaPayload,
) {
  try {
    const { addChildPayload, widgets } = props;
    const isModuleWidget = addChildPayload.type.startsWith("MODULE_WIDGET_");
    const pageId: string = yield select(getCurrentPageId);
    const widget = widgets[addChildPayload.newWidgetId];

    if (isModuleWidget) {
      widgets[addChildPayload.newWidgetId].isCreationPending = true;

      yield put(
        createUIModuleInstance({
          contextId: pageId,
          contextType: ModuleInstanceCreatorType.PAGE,
          widgetName: widget.widgetName,
          widgetId: widget.widgetId,
          parentWidgetId: widget.parentId || "",
          moduleUUID: widget.moduleUUID,
        }),
      );
    }

    return widgets;
  } catch (error) {
    return props.widgets;
  }
}

export function* waitForPackageInitialization(
  saga: Saga,
  action: ReduxAction<unknown>,
) {
  const isPackageEditorInitialized: boolean = yield select(
    getIsPackageEditorInitialized,
  );

  // Wait until the package editor is initialized
  if (!isPackageEditorInitialized) {
    yield take(ReduxActionTypes.INITIALIZE_PACKAGE_EDITOR_SUCCESS);
  }

  // Wait until we're done fetching module entities if applicable
  const isModuleFetchingEntities: boolean = yield select(
    getIsModuleFetchingEntities,
  );

  if (isModuleFetchingEntities) {
    yield take(ReduxActionTypes.FETCH_MODULE_ENTITIES_SUCCESS);
  }

  // Continue yielding
  yield call(saga, action);
}

export function* handleUIModuleWidgetReplay(
  replay: {
    toasts: unknown;
    widgets: unknown;
  },
  widgets: HandleModuleWidgetCreationSagaPayload["widgets"],
) {
  try {
    const widgetsInCanvas: ReturnType<typeof getWidgets> =
      yield select(getWidgets);
    const pageId: string = yield select(getCurrentPageId);

    if (Array.isArray(replay.toasts)) {
      for (const toast of replay.toasts) {
        const { isCreated, widgetId } = toast;

        if (isCreated) {
          const widget = widgets[widgetId];

          if (widget && widget.type.startsWith("MODULE_WIDGET_")) {
            yield put(
              createUIModuleInstance({
                contextId: pageId,
                contextType: ModuleInstanceCreatorType.PAGE,
                widgetName: widget.widgetName,
                widgetId: widget.widgetId,
                parentWidgetId: widget.parentId || "",
                moduleUUID: widget.moduleUUID,
                inputs: widget.inputs,
              }),
            );
          }
        }

        if (!isCreated) {
          const widget = widgetsInCanvas[widgetId];

          if (widget && widget.type.startsWith("MODULE_WIDGET_")) {
            yield put({
              type: ReduxActionTypes.DELETE_UI_MODULE_INSTANCE_BY_WIDGET_INIT,
              payload: {
                widgetId,
              },
            });
          }
        }
      }
    }
  } catch (e) {
    log.error(e);
    appsmithTelemetry.captureException(e, {
      errorName: "UndoRedoUIModuleInstanceSagaError",
    });
  }
}

export function* waitForUpdatingModuleReferences() {
  const isModuleReferenceUpdating: boolean = yield select(
    getIsModuleReferenceUpdating,
  );

  if (isModuleReferenceUpdating) {
    const finishedAction: ReduxAction<unknown> = yield take([
      ReduxActionTypes.MODIFY_MODULE_REFERENCE_BY_NAME_SUCCESS,
      ReduxActionErrorTypes.MODIFY_MODULE_REFERENCE_BY_NAME_ERROR,
    ]);

    if (
      finishedAction.type ===
      ReduxActionErrorTypes.MODIFY_MODULE_REFERENCE_BY_NAME_ERROR
    ) {
      return false;
    }
  }

  return true;
}
